import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useTenantContext } from "./TenantContext";
import { locationApi } from "../services/api";

const LocationContext = createContext(null);

const CURRENT_LOCATION_KEY = "rocket_tronc_current_location";
const SELECTED_TENANT_KEY = "rocket_tronc_selected_tenant";

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  return { success, data };
};

export const LocationProvider = ({ children }) => {
  const { user, isAuthenticated, token, loading: authLoading } = useAuth();
const { currentTenantId } = useTenantContext();

  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocationState] = useState(null);
  // True when the user has explicitly chosen "All Locations" (as opposed to
  // having no location selected yet). Seeded from the __all__ localStorage sentinel
  // so the choice persists across page reloads.
  const [allLocationsChosen, setAllLocationsChosen] = useState(
    () => localStorage.getItem(CURRENT_LOCATION_KEY) === "__all__",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use a ref to track the tenant ID we last loaded for,
  // so we can detect real changes and avoid stale fetches
  const loadedForTenantRef = useRef(null);

  // Monotonically-increasing fetch generation. When a new fetch starts we
  // increment this; callbacks only apply results if their generation still
  // matches, discarding any in-flight responses from a previous tenant.
  const fetchGenRef = useRef(0);

  // Core load function — always reads the tenant from localStorage
  // at call time so it picks up the freshest value
  const loadLocations = useCallback(async () => {
    const tenantIdNow = localStorage.getItem(SELECTED_TENANT_KEY);
    const myGeneration = ++fetchGenRef.current;
    console.log("[LocationContext] loadLocations – tenantId:", tenantIdNow, "gen:", myGeneration);

    setLoading(true);
    setError(null);

    try {
      const response = await locationApi.getDropdown();

      // Discard if a newer fetch has already started (tenant switched mid-flight)
      if (myGeneration !== fetchGenRef.current) {
        console.log("[LocationContext] Discarding stale response for gen:", myGeneration);
        return;
      }

      const { success, data } = extractData(response);

      if (success && data) {
        console.log("[LocationContext] Loaded", data.length, "locations");
        setLocations(data);
      } else {
        console.log("[LocationContext] No locations returned");
        setLocations([]);
      }
    } catch (err) {
      if (myGeneration !== fetchGenRef.current) return; // stale error — ignore
      console.error("[LocationContext] Failed to load locations:", err);
      if (err.status !== 401) {
        setError(err.message || "Failed to load locations");
      }
      setLocations([]);
    } finally {
      if (myGeneration === fetchGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Main effect — fires when auth or tenant changes
  useEffect(() => {
    // Wait for auth to finish
    if (authLoading) return;

    // Not authenticated — reset everything
    if (!isAuthenticated || !user || !token) {
      setLocations([]);
      setCurrentLocationState(null);
      setAllLocationsChosen(false);
      setLoading(false);
loadedForTenantRef.current = null;
      return;
    }

// Tenant changed (or first load) — clear stale data then fetch
    if (currentTenantId !== loadedForTenantRef.current) {
      console.log(
        "[LocationContext] Tenant changed:",
     loadedForTenantRef.current,
        "→",
    currentTenantId,
      );
      const previousTenantId = loadedForTenantRef.current;
      loadedForTenantRef.current = currentTenantId;

      // Immediately clear old data so the UI never shows stale locations.
      // Only wipe the stored location key when switching between real tenants
      // (not on first load/page reload where previous is null — in that case
      // we want to restore the previously selected location from localStorage).
      setLocations([]);
      setCurrentLocationState(null);
      setAllLocationsChosen(false);
      if (previousTenantId !== null) {
        localStorage.removeItem(CURRENT_LOCATION_KEY);
      }

    // Give TenantContext a tick to flush the new tenant ID to localStorage
// so the API client's X-Tenant-Id header picks it up
      const timerId = setTimeout(() => {
        loadLocations();
      }, 150);
      return () => clearTimeout(timerId);
    }
  }, [isAuthenticated, user?.id, token, currentTenantId, authLoading, loadLocations]);

  // Auto-select first location after a fresh load.
  // Skip if the user has explicitly chosen "All Locations" (sentinel value "__all__").
  useEffect(() => {
    if (locations.length > 0 && !currentLocation) {
      // Try to restore the stored location (only if it belongs to the new set)
      const storedId = localStorage.getItem(CURRENT_LOCATION_KEY);
      // "__all__" means the user explicitly chose All Locations — don't override
      if (storedId === "__all__") return;
      if (storedId) {
        const match = locations.find((l) => l.id === storedId);
        if (match) {
          console.log("[LocationContext] Restored location:", match.name);
          setCurrentLocationState(match);
          return;
        }
  }
      // Fall back to first
  console.log("[LocationContext] Auto-selecting:", locations[0].name);
      setCurrentLocationState(locations[0]);
      localStorage.setItem(CURRENT_LOCATION_KEY, locations[0].id);
    }
  }, [locations, currentLocation]);

  const setCurrentLocation = useCallback((location) => {
    console.log("[LocationContext] Set location:", location?.name || "null");
    setCurrentLocationState(location);
    if (location) {
      setAllLocationsChosen(false);
      localStorage.setItem(CURRENT_LOCATION_KEY, location.id);
    } else {
      // User explicitly chose All Locations — store sentinel and flip flag so
      // needsLocationSelection stays false and no modal/prompt appears.
      setAllLocationsChosen(true);
      localStorage.setItem(CURRENT_LOCATION_KEY, "__all__");
    }
  }, []);

  const clearCurrentLocation = useCallback(() => {
    setCurrentLocationState(null);
    setAllLocationsChosen(false);
    localStorage.removeItem(CURRENT_LOCATION_KEY);
  }, []);

  const refreshLocations = useCallback(async () => {
    await loadLocations();
}, [loadLocations]);

  // A location selection is "needed" only when the user hasn't made any choice yet
  // (neither a specific location nor the explicit "All Locations" option).
  const needsLocationSelection =
    !loading && locations.length > 1 && !currentLocation && !allLocationsChosen;
  const hasMultipleLocations = locations.length > 1;
  const hasLocations = locations.length > 0;

  const { isGlobalAdmin, isTenantAdmin } = useAuth();
  const canAccessAllLocations = isGlobalAdmin || isTenantAdmin;
  const currentLocationId = currentLocation?.id ?? null;

  const value = {
    locations,
    currentLocation,
    currentLocationId,
    allLocationsChosen,
    loading,
    error,
    needsLocationSelection,
    hasMultipleLocations,
    hasLocations,
    canAccessAllLocations,
    setCurrentLocation,
    clearCurrentLocation,
  refreshLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(
    "useLocationContext must be used within a LocationProvider",
    );
  }
  return context;
};

export const useLocation = useLocationContext;

export default LocationContext;
